/**
 * 👥 직원 명부 (v2.325.0 · 2026-11-04 사용자 「경영지원부에서 직원들 목록, 연락처 관리」)
 *
 *   누가: 관리자 + 경영지원 결재자 지정(시스템 관리 › 결재선 관리 › 경영지원 (복수)) — 서버 lib/staff-directory.ts.
 *   고치는 칸: 이름 · 직급 · 부서(있는 부서 중에서) · 팀 · 휴대폰. 바뀐 칸만 보낸다.
 *   ⭐계정 권한은 시스템 관리 › 계정 관리(관리자) 그대로 — 역할 · 비밀번호 · 2단계 인증 · 이메일 · 로그인 켜고 끄기.
 *     이메일은 「비밀번호 찾기」 메일이 가는 곳이라 여기서는 보기만.
 *   ⭐부서 글자가 결재선(부서장)·부서별 월간보고서를 정한다 → 직접 쓰지 않고 목록에서 고른다.
 *   누가 언제 무엇을 바꿨는지 = 🕘 변경 이력 「직원」(감사 트리거 seq_0053).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { listStaff, updateStaff, type StaffRow, type StaffUpdateInput } from '../lib/staff-api';
import { useDirtyClose } from '../lib/useDirtyClose';
import { DraggableModal } from '../components/DraggableModal';
import { LoadingLines } from '../components/ui/Skeleton';

export function StaffDirectoryPage() {
  const { user } = useAuth();
  const allowed = user?.role === 'admin' || (user?.role !== 'viewer' && user?.staffDirectory === true);
  const q = useQuery({ queryKey: ['staff'], queryFn: listStaff, enabled: allowed, staleTime: 30_000 });
  const [search, setSearch] = useState('');
  const [showOff, setShowOff] = useState(false);
  const [editing, setEditing] = useState<StaffRow | null>(null);

  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showOff && !r.loginActive) return false;
      if (!s) return true;
      return [r.name, r.title, r.department, r.team, r.mobile, r.email, r.userId]
        .some((v) => (v ?? '').toLowerCase().includes(s));
    });
  }, [rows, search, showOff]);
  const active = rows.filter((r) => r.loginActive);
  const noMobile = active.filter((r) => !r.mobile).length;
  const noDept = active.filter((r) => !r.department).length;
  const offCount = rows.length - active.length;

  if (!allowed) {
    return (
      <div className="p-8">
        <div className="bg-amber-50 border border-amber-200 rounded p-8 text-center">
          <div className="text-4xl mb-2">🔒</div>
          <div className="font-semibold text-amber-900 mb-1">접근 권한 없음</div>
          <div className="text-sm text-amber-700">
            직원 명부는 관리자와 경영지원 담당(시스템 관리 › 결재선 관리 › 경영지원 지정)만 쓸 수 있습니다.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="ui-page-title">직원 명부</h1>
      <p className="ui-page-sub">
        직원 이름·직급·부서·팀·휴대폰을 고칩니다. 아이디·역할·비밀번호·이메일·로그인은 관리자가{' '}
        <span className="text-slate-600 font-medium">[⚙️ 시스템 관리 › 계정 관리]</span>에서 — 누가 고쳤는지는{' '}
        <span className="text-slate-600 font-medium">[🕘 변경 이력]</span>의 「직원」에 남습니다.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-3 mt-3">
        <input
          id="staff-search"
          className="ui-input w-64"
          placeholder="이름·부서·휴대폰 찾기"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />
          로그인 꺼진 계정도 보기{offCount > 0 && <span className="text-slate-400">({offCount})</span>}
        </label>
        {q.data && (
          <span className="text-sm text-slate-600 ml-auto">
            재직 <strong>{active.length}</strong>명
            {noMobile > 0 && <span className="text-amber-700"> · 휴대폰 빈 칸 {noMobile}</span>}
            {noDept > 0 && <span className="text-amber-700"> · 부서 빈 칸 {noDept}</span>}
          </span>
        )}
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-left">이름</th>
              <th className="text-left">직급</th>
              <th className="text-left">부서</th>
              <th className="text-left">팀</th>
              <th className="text-left">휴대폰</th>
              <th className="text-left">이메일</th>
              <th className="text-center w-20">수정</th>
            </tr>
          </thead>
          <tbody>
            {q.isPending && (
              <tr><td colSpan={7} className="py-6"><LoadingLines /></td></tr>
            )}
            {q.isError && (
              <tr><td colSpan={7} className="text-center py-8 text-red-600">불러오기 실패: {(q.error as Error).message}</td></tr>
            )}
            {q.data && shown.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">{search.trim() ? '찾는 직원이 없습니다.' : '직원이 없습니다.'}</td></tr>
            )}
            {shown.map((r) => (
              <tr key={r.userId} className={r.loginActive ? '' : 'bg-slate-50 text-slate-500'}>
                <td className="font-medium whitespace-nowrap">
                  {r.name ?? '-'}
                  {!r.loginActive && <span className="ml-1.5 text-[11px] text-slate-400">로그인 꺼짐</span>}
                </td>
                <td className="whitespace-nowrap">{r.title ?? '-'}</td>
                <td className="whitespace-nowrap">{r.department ?? <span className="text-amber-700">비어 있음</span>}</td>
                <td className="whitespace-nowrap">{r.team ?? '-'}</td>
                <td className="whitespace-nowrap">{r.mobile ?? <span className="text-amber-700">비어 있음</span>}</td>
                <td className="text-slate-500">{r.email ?? '-'}</td>
                <td className="text-center">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(r)}>수정</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && q.data && (
        <StaffEditModal
          row={editing}
          departments={q.data.departments}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** 직원 정보 고치기 — 바뀐 칸만 보낸다 */
function StaffEditModal({ row, departments, onClose }: { row: StaffRow; departments: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(row.name ?? '');
  const [title, setTitle] = useState(row.title ?? '');
  const [department, setDepartment] = useState(row.department ?? '');
  const [team, setTeam] = useState(row.team ?? '');
  const [mobile, setMobile] = useState(row.mobile ?? '');
  const { markDirty, requestClose } = useDirtyClose(onClose);

  // 지금 값이 목록에 없으면(옛 이름) 그 값도 고를 수 있게 둔다 — 그대로 두는 것은 서버도 받는다
  const deptOptions = useMemo(() => {
    const cur = (row.department ?? '').trim();
    return cur && !departments.includes(cur) ? [...departments, cur] : departments;
  }, [departments, row.department]);
  const deptChanged = department !== (row.department ?? '');

  const save = useMutation({
    mutationFn: (input: StaffUpdateInput) => updateStaff(row.userId, input),
    onSuccess: () => {
      // 같은 직원 정보를 보여 주는 목록들 — 시스템 관리 계정 관리 · 직원 고르는 칸(지출결재·회의록 등) · 담당자 고르기
      void qc.invalidateQueries({ queryKey: ['staff'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['expense', 'users'] });
      void qc.invalidateQueries({ queryKey: ['lookup', 'employees'] });
      onClose();
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const input: StaffUpdateInput = {};
    const n = name.trim();
    if (n !== (row.name ?? '')) input.name = n;
    if ((title.trim() || null) !== (row.title ?? null)) input.title = title.trim() || null;
    if ((department || null) !== (row.department ?? null)) input.department = department || null;
    if ((team.trim() || null) !== (row.team ?? null)) input.team = team.trim() || null;
    if ((mobile.trim() || null) !== (row.mobile ?? null)) input.mobile = mobile.trim() || null;
    if (Object.keys(input).length === 0) {
      onClose();
      return;
    }
    save.mutate(input);
  }

  return (
    <DraggableModal onClose={requestClose} initialWidth={520} closeOnBackdrop={false} closeOnEsc={false}>
      <div onInput={markDirty} onChange={markDirty}>
        <div className="dm-drag-handle cursor-move flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
          <h2 className="ui-modal-title">
            직원 정보 수정
            <span className="ml-2 text-xs text-slate-500 font-mono">{row.userId}</span>
          </h2>
          <button type="button" onClick={requestClose} className="ui-close">×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ui-label" htmlFor="staff-name">이름 *</label>
              <input id="staff-name" className="ui-input w-full mt-1" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} required />
            </div>
            <div>
              <label className="ui-label" htmlFor="staff-title">직급</label>
              <input id="staff-title" className="ui-input w-full mt-1" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={50} placeholder="예: 대리" />
            </div>
            <div>
              <label className="ui-label" htmlFor="staff-dept">부서</label>
              <select id="staff-dept" className="ui-input w-full mt-1" value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">(없음)</option>
                {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="ui-label" htmlFor="staff-team">팀</label>
              <input id="staff-team" className="ui-input w-full mt-1" value={team} onChange={(e) => setTeam(e.target.value)} maxLength={50} placeholder="없으면 비워 둠" />
            </div>
          </div>
          {deptChanged && (
            <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5">
              부서를 바꾸면 이 직원이 앞으로 올리는 지출결재의 <b>부서장 단계</b>와 <b>부서별 월간보고서</b>가 새 부서 기준이 됩니다.
              새 부서 이름이 필요하면 관리자에게 요청해 주세요.
            </p>
          )}
          <div>
            <label className="ui-label" htmlFor="staff-mobile">휴대폰</label>
            <input
              id="staff-mobile"
              type="tel"
              inputMode="tel"
              className="ui-input w-full mt-1"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              maxLength={30}
              placeholder="예: 010-1234-5678 (없으면 비워 둠)"
            />
            <p className="text-xs text-slate-500 mt-1">
              메일 꼬리말 회사 정보의 <b>M</b> 과 견적서·발주서 담당자 연락처에 들어갑니다.
            </p>
          </div>
          <p className="text-xs text-slate-500 border-t border-slate-100 pt-2">
            이메일 <span className="text-slate-700">{row.email ?? '(없음)'}</span> · 로그인 {row.loginActive ? '켜짐' : '꺼짐'} —
            이메일·로그인·역할·비밀번호는 관리자가 시스템 관리 › 계정 관리에서 바꿉니다.
          </p>
          {save.error && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded px-2 py-1">{(save.error as Error).message}</div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn btn-secondary" onClick={requestClose}>취소</button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending || !name.trim()}>
              {save.isPending ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </DraggableModal>
  );
}
